import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useDeleteConnector,
  usePatchConnector,
  useTestConnector,
} from "@/features/connectors/hooks/use-connectors";
import type { Connector } from "@/features/connectors/types";
import {
  CheckCircle,
  DotsThree,
  Pause,
  PencilSimple,
  Play,
  Trash,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { EditConnectorDialog } from "./edit-connector-dialog";
import { useTranslation } from "@/features/utils/i18n";

export const Action = ({ connector }: { connector: Connector }) => {
  const { t } = useTranslation("connectors");
  const { company: clientId } = useParams<{ company: string }>();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const deleteConnector = useDeleteConnector();
  const patchConnector = usePatchConnector();
  const testConnector = useTestConnector();

  const handleDelete = async () => {
    if (!clientId) return;

    try {
      await deleteConnector.mutateAsync({
        clientId,
        connectorId: connector.id!,
      });
      toast.success(t("messages.deleted"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.deleteError"),
      );
    }
  };

  const handleToggleStatus = async () => {
    if (!clientId) return;

    const newStatus = connector.status === "active" ? "inactive" : "active";
    try {
      await patchConnector.mutateAsync({
        clientId,
        connectorId: connector.id!,
        data: { status: newStatus },
      });
      toast.success(
        newStatus === "active"
          ? t("messages.activated")
          : t("messages.deactivated"),
      );
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.updateError"),
      );
    }
  };

  const handleTest = async () => {
    if (!clientId) return;

    try {
      const result = await testConnector.mutateAsync({
        clientId,
        connectorId: connector.id!,
      });

      if (result.success) {
        toast.success(result.message || t("messages.testSuccess"));
      } else {
        toast.error(result.message || t("messages.testFailed"));
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.testError"),
      );
    }
  };

  return (
    <div className="text-right flex items-center justify-end gap-2">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setEditDialogOpen(true)}
      >
        <PencilSimple className="h-4 w-4" weight="fill" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <DotsThree className="h-4 w-4" weight="bold" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleTest}>
            <CheckCircle className="h-4 w-4" weight="fill" />
            {t("actions.test")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleToggleStatus}>
            {connector.status === "active" ? (
              <>
                <Pause className="h-4 w-4" weight="fill" />
                {t("actions.deactivate")}
              </>
            ) : (
              <>
                <Play className="h-4 w-4" weight="fill" />
                {t("actions.activate")}
              </>
            )}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleDelete} className="text-red-500">
            <Trash className="h-4 w-4" weight="fill" />
            {t("actions.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <EditConnectorDialog
        connector={connector}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />
    </div>
  );
};
